import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Optional,
  Output,
  inject
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { faCircleCheck, faCheck, faEye as faEyeSolid } from '@fortawesome/free-solid-svg-icons';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { finalize, takeUntil } from 'rxjs';

import { ENVIRONMENT } from '@environment';
import { AuthService } from '@usheru-hff/authentication/domain';
import { UserInteractionService } from '@usheru-hff/community-elements/utils';
import { getMovieGenres, getMovieTags, Movie } from '@usheru-hff/shared/domain';
import { UserCtaDirective } from '@usheru-hff/shared/utils-authentication';
import { TrackingService } from '@usheru-hff/sdk/tracking';

@Component({
  selector: 'ui-movie-card-watched-button',
  templateUrl: './ui-movie-card-watched-button.component.html',
  styleUrls: ['./ui-movie-card-watched-button.component.scss'],
  standalone: true,
  imports: [CommonModule, ButtonModule, TooltipModule, UserCtaDirective, FontAwesomeModule],
  // encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiMovieCardWatchedButtonComponent {
  @Input() enabled?: boolean;
  @Input({ required: true }) movie!: Movie;
  @Input() type: 'button' | 'icon' = 'button';

  @Output() statusChange = new EventEmitter<boolean>();

  // private readonly messageService = inject(MessageService);
  private readonly userInteractionService = inject(UserInteractionService);
  private readonly trackingService = inject(TrackingService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(AuthService);

  readonly markAsWatchedLabel = $localize`:@@button.to-watched:Watched`;
  // readonly unwatchMovieLabel = $localize`:Unmark movie as watched button text@@button.unwatch:Unwatch`;
  readonly markAsWatchedTooltip = $localize`:@@feature-film-interactions.watched.add.tooltip:Mark movie as watched`;
  readonly unwatchMovieTooltip = $localize`:@@feature-film-interactions.watched.saved:Movie marked as watched`;

  readonly TRACKING_CATEGORY = 'Community';
  readonly TRACKING_ON_ADD_TO_WATCHED = 'AddToWatched';
  readonly TRACKING_ON_REMOVE_FROM_WATCHED = 'RemoveFromWatched';

  loading = false;
  focus = false;
  circularCheckIcon = faCircleCheck;
  checkIcon = faCheck;
  eyeIcon = faEye;
  eyeIconSolid = faEyeSolid;

  hintOptions: any = {
    showDelay: 500
  };

  destroyed$ = new EventEmitter();

  constructor(@Optional() private messageService: MessageService) {}

  onAuthorizedClick() {}

  addMovieToWatched() {
    this.loading = true;

    const onComplete = () => {
      this.messageService.add({
        severity: 'success',
        summary: $localize`:@@ui-movie-card-watched.watched.success.title:Success`,
        detail: $localize`:@@ui-movie-card-watched.watched.success.description:Movie marked as watched`
      });

      this.loading = false;
      this.movie.inMyWatched = true;
      this.statusChange.emit(true);
      this.trackEvent(this.TRACKING_ON_ADD_TO_WATCHED);
    };

    const onError = () => {
      this.messageService.add({
        severity: 'error',
        summary: $localize`:@@ui-movie-card-watched.watched.error.title:Error`,
        detail: $localize`:@@ui-movie-card-watched.watched.error.description:Movie could not be added to watched`
      });
      this.loading = false;
    };

    this.userInteractionService
      .addMovieToWatched({ idMovie: this.movie.id })
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.focus = false;
          this.cd.markForCheck();
        })
      )
      .subscribe({
        next: () => onComplete(),
        error: () => onError()
      });
  }

  removeMovieFromWatched() {
    this.loading = true;

    const onComplete = () => {
      this.messageService.add({
        severity: 'success',
        summary: $localize`:@@watched-collection.remove-item.success.title:Success`,
        detail: $localize`:@@watched-collection.remove-item.success.description:Movie removed from watched`
      });

      this.loading = false;
      this.movie.inMyWatched = false;
      this.statusChange.emit(true);
      this.trackEvent(this.TRACKING_ON_REMOVE_FROM_WATCHED);
    };

    const onError = () => {
      this.messageService.add({
        severity: 'error',
        summary: $localize`:@@watched-collection.remove-item.error.title:Error`,
        detail: $localize`:@@watched-collection.remove-item.error.description:Movie could not be removed from watched`
      });

      this.loading = false;
    };

    this.userInteractionService
      .removeMovieFromWatched(this.movie.id)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.focus = false;
          this.cd.markForCheck();
        })
      )
      .subscribe({
        next: () => onComplete(),
        error: () => onError()
      });
  }

  onToggleButton() {
    if (this.movie.inMyWatched) {
      this.removeMovieFromWatched();
    } else {
      this.addMovieToWatched();
    }
  }

  trackEvent(action: string) {
    this.trackingService.trackEvent({
      action: action,
      properties: {
        category: this.TRACKING_CATEGORY,
        // label: '',
        movie: this.movie.title || '',
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie),
        user: this.username
      }
    });
  }

  get username() {
    return this.authService.getUserUsername();
  }

  get isWatched() {
    return this.movie.inMyWatched || this.enabled;
  }

  get watchedIcon() {
    const selectedIcon = this.env.communityConfig?.filmInteractionsConfig?.watchedIcon || 'check';
    return selectedIcon === 'eye' ? this.eyeIcon : this.checkIcon;
  }

  get watchedIconSolid() {
    const selectedIcon = this.env.communityConfig?.filmInteractionsConfig?.watchedIcon || 'check';
    return selectedIcon === 'eye' ? this.eyeIconSolid : this.circularCheckIcon;
  }
}
